import { Box, GlobalStyles, Stack, Typography } from '@mui/material';
import {
  ImageBox,
  TixlyIconButton,
  ToastTimer,
} from '@tix-ticketing/ui-components';
import { CrossIcon } from '@tix-ticketing/ui-icons/outline';
import type { CustomContentProps } from 'notistack';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { forwardRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { ProductDto } from 'api/generated/types.gen';
import {
  headerHeight,
  headerHeightSmall,
} from 'components/common/navbar/navbar';
import { useAppState } from 'lib/app-state';
import { FormattedPrice } from 'lib/helpers/formatted-price';
import { getVariantOptionLabels } from 'lib/helpers/product-helpers';

export type ProductAddedToastOptions = {
  imageUrl?: string;
  name: string;
  /** One "Variation: Option" label per chosen option. */
  options: string[];
  unitPrice: number;
  quantity: number;
};

/** Class given to the snackbar container the product toasts stack in. */
export const productAddedToastContainerClass = 'product-added-toasts';

// notistack pads every snackbar in its container by this much above and below.
const snackbarPadding = 6;

/**
 * Confirmation that a product went into the cart: its artwork, name, chosen
 * options, unit price and the quantity added. Rendered by notistack for the
 * `productAdded` variant.
 */
export const ProductAddedToast = forwardRef<
  HTMLDivElement,
  CustomContentProps & ProductAddedToastOptions
>(function ProductAddedToast(
  {
    id,
    autoHideDuration,
    imageUrl,
    name,
    options,
    unitPrice,
    quantity,
    // The enter and exit transition's styles, e.g. a fade's opacity.
    style,
  },
  ref
) {
  const intl = useIntl();

  return (
    <Stack
      ref={ref}
      style={style}
      role="status"
      data-testid="product-added-toast"
      sx={(theme) => ({
        position: 'relative',
        overflow: 'hidden',
        gap: 1,
        width: 420,
        pt: 2,
        pb: 3,
        pl: 3,
        pr: 2,
        backgroundColor: theme.palette.background.elevated,
        borderRadius: `${theme.containerRadius.main}px`,
        boxShadow: theme.shadows[8],
        [theme.breakpoints.down('sm')]: {
          width: '100%',
          pt: 1,
          pb: 1.5,
          px: 2,
        },
      })}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography variant="body1Bold">
          <FormattedMessage
            id="products.toast.addedToCart"
            defaultMessage="Added to your cart"
          />
        </Typography>
        <TixlyIconButton
          size="small"
          variant="text"
          color="monochrome"
          onClick={() => closeSnackbar(id)}
          aria-label={intl.formatMessage({
            id: 'common.close',
            defaultMessage: 'Close',
          })}
        >
          <CrossIcon />
        </TixlyIconButton>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pr: 1 }}>
        <ImageBox
          imageUrl={imageUrl}
          altText=""
          variant="thumbnail"
          sx={{ width: 64, height: 64, flexShrink: 0 }}
        />
        <Stack sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
          <Typography variant="body2Bold" sx={{ overflowWrap: 'anywhere' }}>
            {name}
          </Typography>
          {options.length > 0 && (
            <Stack>
              {options.map((option) => (
                <Typography
                  key={option}
                  variant="body3"
                  sx={{ color: 'text.secondary', overflowWrap: 'anywhere' }}
                >
                  {option}
                </Typography>
              ))}
            </Stack>
          )}
        </Stack>
        <Stack sx={{ alignItems: 'flex-end', gap: 0.25, flexShrink: 0 }}>
          <Typography variant="body2Bold">
            <FormattedPrice amount={unitPrice} />
          </Typography>
          <Typography variant="body3" sx={{ color: 'text.secondary' }}>
            <FormattedMessage
              id="products.toast.quantity"
              defaultMessage="x{quantity}"
              values={{ quantity }}
            />
          </Typography>
        </Stack>
      </Box>
      {autoHideDuration ? (
        <ToastTimer duration={autoHideDuration} color="success.main" />
      ) : null}
    </Stack>
  );
});

/**
 * Places the product toasts just below the navbar: in the top-right corner on
 * desktop, and across the full width on mobile.
 */
export function ProductAddedToastPosition() {
  const { navbarExtraHeight, navbarBannerHeight } = useAppState();
  // Where the first toast's container starts for the toast to clear the navbar.
  const below = (navbarHeight: number) =>
    navbarHeight + navbarExtraHeight + navbarBannerHeight - snackbarPadding;

  return (
    <GlobalStyles
      styles={(theme) => ({
        // Doubled to outrank notistack's own container position.
        [`.${productAddedToastContainerClass}.${productAddedToastContainerClass}`]:
          {
            top: `calc(${below(headerHeight)}px + ${theme.spacing(3)})`,
            right: theme.spacing(3),
            [theme.breakpoints.down('sm')]: {
              top: `calc(${below(headerHeightSmall)}px + ${theme.spacing(1.5)})`,
              left: theme.spacing(1.5),
              right: theme.spacing(1.5),
              maxWidth: `calc(100% - ${theme.spacing(3)})`,
            },
          },
      })}
    />
  );
}

/**
 * Shows the toast for a product variant just added to the cart, at its catalog
 * price. Returns the toast's key, to take it back should the add be rejected.
 */
export function showProductAddedToast({
  product,
  productVariantId,
  quantity,
}: {
  product: ProductDto;
  productVariantId: number;
  quantity: number;
}) {
  const variant = product.variants?.find(
    (v) => v.productVariantId === productVariantId
  );
  return enqueueSnackbar({
    variant: 'productAdded',
    anchorOrigin: { vertical: 'top', horizontal: 'right' },
    autoHideDuration: 6000,
    imageUrl: product.imageUrl ?? undefined,
    name: product.name ?? '',
    options: variant ? getVariantOptionLabels(product, variant) : [],
    unitPrice: variant?.price ?? product.price ?? 0,
    quantity,
  });
}
